import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { TranslocoService } from '@jsverse/transloco';
import {
	BehaviorSubject,
	catchError,
	concat,
	first,
	interval,
	merge,
	of,
	switchMap,
	tap,
} from 'rxjs';

const HOUR = 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ServiceWorkerService {
	private readonly updates = inject(SwUpdate);
	private readonly appRef = inject(ApplicationRef);
	private readonly transloco = inject(TranslocoService);

	/**
	 * Will trigger every hour since the initialization of this component, until the destruction of the
	 * app. Since this component is used as the root it is a good source of truth as to the age of the app instance
	 */
	private readonly everyHour$ = interval(HOUR);
	private readonly isStable$ = this.appRef.isStable.pipe(
		first((isStable) => isStable === true),
	);

	/**
	 * When the active service worker ends up in a broken/unrecoverable state the
	 * app shell can fail to load (a classic "stuck on reload" symptom). Reload once
	 * to recover, guarding against an infinite reload loop if it stays broken.
	 */
	private readonly unrecoverable$ = this.updates.unrecoverable.pipe(
		tap(() => this.reloadOnce('unrecoverable')),
		switchMap(() => of(false)),
	);

	readonly updateAvailable$ = new BehaviorSubject(false);
	readonly checkForUpdate$ = merge(
		concat(this.isStable$, this.everyHour$).pipe(
			switchMap(() => {
				if (!this.updates.isEnabled) return of(false);
				return this.updates.checkForUpdate();
			}),
		),
		this.unrecoverable$,
	).pipe(
		catchError(() => of(false)),
		tap((updateFound) => {
			if (updateFound) {
				const confirmed = window.confirm(
					this.transloco.translate('app.alert.new-version'),
				);
				if (confirmed) this.activateNewVersion();
				else this.updateAvailable$.next(true);
			} else if (this.updateAvailable$.value === true) {
				this.updateAvailable$.next(false);
			}
		}),
	);

	async activateNewVersion() {
		// Swap to the freshly downloaded version before reloading. Reloading
		// without activating can serve the old shell against new lazy chunks,
		// causing chunk-load failures / hangs after a deploy.
		try {
			await this.updates.activateUpdate();
		} catch {
			// Activate is best-effort; reload regardless to pick up the new shell.
		}
		document.location.reload();
	}

	/**
	 * Reload at most once per browser session for a given reason, so a persistently
	 * broken state can't trap the user in a reload loop.
	 */
	private reloadOnce(reason: string) {
		const key = `sw-reload:${reason}`;
		try {
			if (sessionStorage.getItem(key)) return;
			sessionStorage.setItem(key, Date.now().toString());
		} catch {
			// sessionStorage unavailable (private mode / SSR) — fall through.
		}
		document.location.reload();
	}
}
